import { useState } from 'react';
import { Card } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Switch } from '@/components/ui/switch';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { CheckCircle2, XCircle, ArrowUpDown, Search, Download, GitCompare } from 'lucide-react';
import { useFlexESimulation } from '@/hooks/useFlexESimulation';
import { Skeleton } from '@/components/ui/skeleton';
import FlexEDayDetailModal from './FlexEDayDetailModal';
import * as XLSX from 'xlsx';

interface FlexEResultsTableProps {
  simId: string;
}

type SortKey = 'date' | 'max_deficit_kw' | 'deficit_kwh' | 'passed';
type SortOrder = 'asc' | 'desc';

export default function FlexEResultsTable({ simId }: FlexEResultsTableProps) {
  const { simulation, results, isLoading } = useFlexESimulation(simId);
  const [selectedDate, setSelectedDate] = useState<string | null>(null);
  const [sortKey, setSortKey] = useState<SortKey>('date');
  const [sortOrder, setSortOrder] = useState<SortOrder>('asc');
  const [filterStatus, setFilterStatus] = useState<'all' | 'passed' | 'failed'>('all');
  const [searchQuery, setSearchQuery] = useState('');
  const [showComparison, setShowComparison] = useState(false);

  const hasBaselineData = simulation?.baseline_success_rate != null;

  if (isLoading || !simulation || !results) {
    return (
      <Card className="p-6">
        <Skeleton className="h-64 w-full" />
      </Card>
    );
  }

  // Filter results
  let filteredResults = results;

  if (filterStatus !== 'all') {
    filteredResults = results.filter(r => 
      filterStatus === 'passed' ? r.passed : !r.passed
    );
  }

  if (searchQuery) {
    filteredResults = filteredResults.filter(r =>
      r.date.includes(searchQuery)
    );
  }

  // Sort results
  const sortedResults = [...filteredResults].sort((a, b) => {
    let aVal: any = a[sortKey];
    let bVal: any = b[sortKey];

    if (sortKey === 'passed') {
      aVal = a.passed ? 1 : 0;
      bVal = b.passed ? 1 : 0;
    }

    if (aVal < bVal) return sortOrder === 'asc' ? -1 : 1;
    if (aVal > bVal) return sortOrder === 'asc' ? 1 : -1;
    return 0;
  });

  const handleSort = (key: SortKey) => {
    if (sortKey === key) {
      setSortOrder(sortOrder === 'asc' ? 'desc' : 'asc');
    } else {
      setSortKey(key);
      setSortOrder('asc');
    }
  };

  const exportToExcel = () => {
    const exportData = sortedResults.map(r => ({
      Datum: new Date(r.date).toLocaleDateString('nl-NL'),
      'Max Overschrijding (kW)': r.max_deficit_kw.toFixed(2),
      'Energiebehoefte (kWh)': r.deficit_kwh.toFixed(2),
      'Kwartieren': r.deficit_quarters || 0,
      Resultaat: r.passed ? 'Geslaagd' : 'Gefaald',
      Reden: r.failure_reason || '-'
    }));

    const ws = XLSX.utils.json_to_sheet(exportData);
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, 'Resultaten');
    XLSX.writeFile(wb, `flexe-simulatie-${simulation.name}-${new Date().toISOString().split('T')[0]}.xlsx`);
  };

  const selectedResult = selectedDate 
    ? results.find(r => r.date === selectedDate)
    : null;

  return (
    <>
      <Card className="p-6">
        <div className="flex items-center justify-between mb-4">
          <h3 className="text-lg font-semibold">Dagresultaten</h3>
          <div className="flex gap-2 items-center">
            {hasBaselineData && simulation.scenario_applied && (
              <div className="flex items-center gap-2 mr-2 pr-2 border-r border-border">
                <Label htmlFor="comparison-toggle" className="text-sm cursor-pointer flex items-center gap-1">
                  <GitCompare className="h-3 w-3" />
                  Toon vergelijking
                </Label>
                <Switch 
                  id="comparison-toggle"
                  checked={showComparison} 
                  onCheckedChange={setShowComparison}
                />
              </div>
            )}
            <Button
              variant={filterStatus === 'all' ? 'default' : 'outline'}
              size="sm"
              onClick={() => setFilterStatus('all')}
            >
              Alle
            </Button>
            <Button
              variant={filterStatus === 'passed' ? 'default' : 'outline'}
              size="sm"
              onClick={() => setFilterStatus('passed')}
            >
              Geslaagd
            </Button>
            <Button
              variant={filterStatus === 'failed' ? 'default' : 'outline'}
              size="sm"
              onClick={() => setFilterStatus('failed')}
            >
              Gefaald
            </Button>
            <Button variant="outline" size="sm" onClick={exportToExcel}>
              <Download className="h-4 w-4 mr-2" />
              Excel
            </Button>
          </div>
        </div>

        <div className="mb-4">
          <div className="relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
            <Input
              placeholder="Zoek datum..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="pl-9"
            />
          </div>
        </div>

        <div className="border rounded-lg overflow-hidden">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => handleSort('date')}
                    className="h-8 px-2"
                  >
                    Datum
                    <ArrowUpDown className="ml-2 h-3 w-3" />
                  </Button>
                </TableHead>
                {showComparison && hasBaselineData && (
                  <>
                    <TableHead>
                      <Button
                        variant="ghost"
                        size="sm"
                        className="h-8 px-2"
                      >
                        Baseline (kWh)
                      </Button>
                    </TableHead>
                    <TableHead>
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => handleSort('deficit_kwh')}
                        className="h-8 px-2"
                      >
                        Met Scenario (kWh)
                        <ArrowUpDown className="ml-2 h-3 w-3" />
                      </Button>
                    </TableHead>
                    <TableHead>
                      <Button
                        variant="ghost"
                        size="sm"
                        className="h-8 px-2"
                      >
                        Verschil (kWh)
                      </Button>
                    </TableHead>
                  </>
                )}
                {!showComparison && (
                  <>
                    <TableHead>
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => handleSort('max_deficit_kw')}
                        className="h-8 px-2"
                      >
                        Max Overschrijding (kW)
                        <ArrowUpDown className="ml-2 h-3 w-3" />
                      </Button>
                    </TableHead>
                    <TableHead>
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => handleSort('deficit_kwh')}
                        className="h-8 px-2"
                      >
                        Energiebehoefte (kWh)
                        <ArrowUpDown className="ml-2 h-3 w-3" />
                      </Button>
                    </TableHead>
                  </>
                )}
                <TableHead>
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => handleSort('passed')}
                    className="h-8 px-2"
                  >
                    Resultaat
                    <ArrowUpDown className="ml-2 h-3 w-3" />
                  </Button>
                </TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {sortedResults.map((result) => (
                <TableRow
                  key={result.id}
                  className="cursor-pointer hover:bg-muted/50"
                  onClick={() => setSelectedDate(result.date)}
                >
                  <TableCell className="font-medium">
                    {new Date(result.date).toLocaleDateString('nl-NL', {
                      weekday: 'short',
                      day: '2-digit',
                      month: '2-digit',
                      year: 'numeric'
                    })}
                  </TableCell>
                  {showComparison && hasBaselineData && (
                    <>
                      <TableCell className="text-right font-mono text-red-600 dark:text-red-400">
                        {(result as any).baseline_deficit_kwh?.toFixed(2) || '-'}
                      </TableCell>
                      <TableCell className="text-right font-mono text-green-600 dark:text-green-400">
                        {result.deficit_kwh.toFixed(2)}
                      </TableCell>
                      <TableCell className="text-right font-mono font-semibold text-blue-600 dark:text-blue-400">
                        {((result as any).baseline_deficit_kwh 
                          ? (result.deficit_kwh - (result as any).baseline_deficit_kwh).toFixed(2)
                          : '-')}
                      </TableCell>
                    </>
                  )}
                  {!showComparison && (
                    <>
                      <TableCell className="text-right font-mono">
                        {result.max_deficit_kw.toFixed(2)}
                      </TableCell>
                      <TableCell className="text-right font-mono">
                        {result.deficit_kwh.toFixed(2)}
                      </TableCell>
                    </>
                  )}
                  <TableCell>
                    {result.passed ? (
                      <div className="flex items-center gap-2 text-green-600 dark:text-green-400">
                        <CheckCircle2 className="h-4 w-4" />
                        Geslaagd
                      </div>
                    ) : (
                      <div className="flex items-center gap-2 text-red-600 dark:text-red-400">
                        <XCircle className="h-4 w-4" />
                        Gefaald
                      </div>
                    )}
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </div>

        <p className="text-sm text-muted-foreground mt-4">
          Klik op een rij om gedetailleerde kwartierdata te bekijken
        </p>
      </Card>

      {selectedResult && simulation && (
        <FlexEDayDetailModal
          result={selectedResult}
          simulation={simulation}
          onClose={() => setSelectedDate(null)}
        />
      )}
    </>
  );
}
